import type { EmblaOptionsType } from "embla-carousel";
import EmblaCarousel from "embla-carousel";
import { addPrevNextBtnsClickHandlers } from "./modules/embla-carousel-arrow-buttons";
import Plyr from "plyr";
import "plyr/dist/plyr.css";

(function () {
  const applySingleSlideScaleEffect = (
    slideNode: HTMLElement,
    distanceFromCenter: number,
  ) => {
    const scale = Math.max(0, 1 - distanceFromCenter * 0.2);

    slideNode.style.transform = `scale(${scale})`;
    slideNode.style.transition = "transform 300ms ease";
    slideNode.style.transformOrigin = "center center";
  };

  const applySlideScaleEffect = (
    emblaNode: HTMLElement,
    centerIndex: number,
  ) => {
    const slideNodes = emblaNode.querySelectorAll<HTMLElement>(".embla__slide");
    const slidesCount = slideNodes.length;
    if (!slidesCount) return;

    if (window.innerWidth < 768) {
      slideNodes.forEach((slideNode) => {
        applySingleSlideScaleEffect(slideNode, 0);
      });

      return;
    }

    slideNodes.forEach((slideNode, index) => {
      const distanceFromCenter = Math.abs(index - centerIndex);
      applySingleSlideScaleEffect(slideNode, distanceFromCenter);
    });
  };

  const loadEmblaCarousel = () => {
    const emblaNodes = document.querySelectorAll<HTMLElement>(".embla");
    if (!emblaNodes.length) return;

    emblaNodes.forEach((emblaNode) => {
      const columnsPerPageRaw = emblaNode.dataset.columnsPerPage;
      if (!columnsPerPageRaw) {
        throw new Error(
          "Missing `data-columns-per-page` attribute on carousel container.",
        );
      }

      const isMobile = window.innerWidth < 768;

      const columnsPerPage = isMobile
        ? 1
        : Number.parseInt(columnsPerPageRaw, 10);
      if (!Number.isInteger(columnsPerPage) || columnsPerPage <= 0) {
        throw new Error(
          `Invalid \`data-columns-per-page\` value: "${columnsPerPageRaw}". Expected a positive integer.`,
        );
      }

      const options: EmblaOptionsType = {
        loop: false,
        slidesToScroll: 1,
        // On mobile, exclude placeholder slides from Embla to avoid snap issues
        // when placeholders are hidden with `display: none`.
        slides: isMobile ? ".embla__slide__real" : ".embla__slide",
        startIndex: !isMobile
          ? emblaNode.querySelectorAll<HTMLElement>(".embla__slide__real")
              .length -
              1 >
            1
            ? 1
            : 0
          : 0,
      };
      const viewportNode = emblaNode.querySelector(
        ".embla__viewport",
      ) as HTMLElement | null;
      const prevBtnNode = emblaNode.querySelector(
        ".embla__button--prev",
      ) as HTMLButtonElement | null;
      const nextBtnNode = emblaNode.querySelector(
        ".embla__button--next",
      ) as HTMLButtonElement | null;
      if (!viewportNode || !prevBtnNode || !nextBtnNode) return;

      const emblaApi = EmblaCarousel(viewportNode, options);
      const updateScaleEffect = () => {
        const selectedSnapIndex = emblaApi.selectedScrollSnap();
        const centerOffset = Math.floor(columnsPerPage / 2);
        const centerIndex = Math.min(
          selectedSnapIndex + centerOffset,
          emblaApi.slideNodes().length - 1,
        );

        applySlideScaleEffect(emblaNode, centerIndex);
      };
      const updateArrowVisibility = () => {
        const selectedSnapIndex = emblaApi.selectedScrollSnap();
        const slidesCount = emblaApi.slideNodes().length;
        const lastVisibleStartIndex = Math.max(slidesCount - columnsPerPage, 0);
        const hasPrev = selectedSnapIndex > 0;
        const hasNext = selectedSnapIndex < lastVisibleStartIndex;

        prevBtnNode.classList.toggle("invisible", !hasPrev);
        prevBtnNode.disabled = !hasPrev;
        prevBtnNode.setAttribute("aria-hidden", String(!hasPrev));

        nextBtnNode.classList.toggle("invisible", !hasNext);
        nextBtnNode.disabled = !hasNext;
        nextBtnNode.setAttribute("aria-hidden", String(!hasNext));
      };

      const removePrevNextBtnsClickHandlers = addPrevNextBtnsClickHandlers(
        emblaApi,
        prevBtnNode,
        nextBtnNode,
      );

      emblaApi
        .on("init", updateScaleEffect)
        .on("select", updateScaleEffect)
        .on("reInit", updateScaleEffect)
        .on("init", updateArrowVisibility)
        .on("select", updateArrowVisibility)
        .on("reInit", updateArrowVisibility);

      updateScaleEffect();
      updateArrowVisibility();

      emblaApi.on("destroy", removePrevNextBtnsClickHandlers);

      window.addEventListener("resize", () => {
        if (window.innerWidth < 768) {
          applySlideScaleEffect(emblaNode, 0);
        }
      });
    });
  };

  const loadVideoPlayers = () => {
    const videoPlayers = document.querySelectorAll<HTMLElement>(
      ".carousel-video",
    ) as NodeListOf<HTMLElement>;

    for (const videoPlayer of videoPlayers) {
      new Plyr(videoPlayer);
    }
  };

  const loadImagePreview = () => {
    const previewImages = document.querySelectorAll<HTMLImageElement>(
      "img[data-image-preview]",
    );
    if (!previewImages.length) return;

    const galleries = new Map<string, HTMLImageElement[]>();
    previewImages.forEach((image) => {
      const galleryId = image.dataset.imagePreview;
      if (!galleryId) {
        throw new Error("Missing `data-image-preview` value on preview image.");
      }

      const gallery = galleries.get(galleryId) ?? [];
      gallery.push(image);
      galleries.set(galleryId, gallery);
    });

    const FADE_MS = 300;
    let activeGalleryId: string | null = null;
    let activeIndex = 0;
    let isOpen = false;
    let closeTimeoutId: number | null = null;

    const overlay = document.createElement("div");
    overlay.className =
      "fixed inset-0 z-[100] flex items-center justify-center bg-black/40 opacity-0 pointer-events-none transition-opacity duration-300";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-hidden", "true");
    overlay.setAttribute("aria-label", "Image preview");

    overlay.innerHTML = `
      <button type="button" data-preview-close class="absolute top-4 right-4 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white/90 text-muted-400 transition-colors hover:bg-white" aria-label="Close preview">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <button type="button" data-preview-prev class="absolute left-2 sm:left-6 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white/90 text-primary-400 transition-opacity disabled:cursor-not-allowed disabled:opacity-40" aria-label="Previous image">
        <svg width="20" height="20" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M30 16H2M16 2L2 16L16 30" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <div class="flex max-h-[90vh] max-w-[90vw] flex-col items-center gap-4 px-14 sm:px-20">
        <img data-preview-image class="max-h-[80vh] max-w-full object-contain" alt="" />
        <p data-preview-counter class="font-roboto text-base text-white tabular-nums" aria-live="polite"></p>
      </div>
      <button type="button" data-preview-next class="absolute right-2 sm:right-6 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white/90 text-primary-400 transition-opacity disabled:cursor-not-allowed disabled:opacity-40" aria-label="Next image">
        <svg width="20" height="20" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M2 16H30M16 2L30 16L16 30" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    document.body.appendChild(overlay);

    const previewImage = overlay.querySelector<HTMLImageElement>(
      "[data-preview-image]",
    );
    const counterNode = overlay.querySelector<HTMLElement>(
      "[data-preview-counter]",
    );
    const closeBtn = overlay.querySelector<HTMLButtonElement>(
      "[data-preview-close]",
    );
    const prevBtn = overlay.querySelector<HTMLButtonElement>(
      "[data-preview-prev]",
    );
    const nextBtn = overlay.querySelector<HTMLButtonElement>(
      "[data-preview-next]",
    );

    if (!previewImage || !counterNode || !closeBtn || !prevBtn || !nextBtn) {
      throw new Error("Image preview overlay is missing required elements.");
    }

    const getActiveGallery = (): HTMLImageElement[] => {
      if (!activeGalleryId) {
        throw new Error("No active image preview gallery.");
      }

      const gallery = galleries.get(activeGalleryId);
      if (!gallery?.length) {
        throw new Error(
          `Image preview gallery "${activeGalleryId}" is empty or missing.`,
        );
      }

      return gallery;
    };

    const updateView = () => {
      const gallery = getActiveGallery();
      const currentImage = gallery[activeIndex];
      if (!currentImage) {
        throw new Error(
          `Image preview index ${activeIndex} is out of range for gallery "${activeGalleryId}".`,
        );
      }

      const previewSrc = currentImage.dataset.previewSrc;
      if (!previewSrc) {
        throw new Error(
          "Missing `data-preview-src` attribute on preview image.",
        );
      }

      previewImage.src = previewSrc;
      previewImage.alt = currentImage.alt;
      counterNode.textContent = `${activeIndex + 1}/${gallery.length}`;

      const hasPrev = activeIndex > 0;
      const hasNext = activeIndex < gallery.length - 1;
      prevBtn.disabled = !hasPrev;
      nextBtn.disabled = !hasNext;
    };

    const openPreview = (galleryId: string, index: number) => {
      const gallery = galleries.get(galleryId);
      if (!gallery?.length) {
        throw new Error(
          `Cannot open image preview gallery "${galleryId}": gallery is empty or missing.`,
        );
      }
      if (index < 0 || index >= gallery.length) {
        throw new Error(
          `Cannot open image preview at index ${index} for gallery "${galleryId}".`,
        );
      }

      if (closeTimeoutId !== null) {
        window.clearTimeout(closeTimeoutId);
        closeTimeoutId = null;
      }

      activeGalleryId = galleryId;
      activeIndex = index;
      isOpen = true;
      updateView();

      overlay.setAttribute("aria-hidden", "false");
      overlay.classList.remove("pointer-events-none");
      // Force a reflow so the opacity transition runs after pointer-events change.
      void overlay.offsetWidth;
      overlay.classList.remove("opacity-0");
      overlay.classList.add("opacity-100");
    };

    const closePreview = () => {
      if (!isOpen) return;

      isOpen = false;
      overlay.classList.remove("opacity-100");
      overlay.classList.add("opacity-0");
      overlay.setAttribute("aria-hidden", "true");

      closeTimeoutId = window.setTimeout(() => {
        overlay.classList.add("pointer-events-none");
        activeGalleryId = null;
        closeTimeoutId = null;
      }, FADE_MS);
    };

    const showPrev = () => {
      if (!isOpen || activeIndex <= 0) return;
      activeIndex -= 1;
      updateView();
    };

    const showNext = () => {
      if (!isOpen) return;
      const gallery = getActiveGallery();
      if (activeIndex >= gallery.length - 1) return;
      activeIndex += 1;
      updateView();
    };

    galleries.forEach((gallery, galleryId) => {
      gallery.forEach((image, index) => {
        image.addEventListener("click", (event) => {
          event.preventDefault();
          openPreview(galleryId, index);
        });
      });
    });

    closeBtn.addEventListener("click", (event) => {
      event.stopPropagation();
      closePreview();
    });
    prevBtn.addEventListener("click", (event) => {
      event.stopPropagation();
      showPrev();
    });
    nextBtn.addEventListener("click", (event) => {
      event.stopPropagation();
      showNext();
    });

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) {
        closePreview();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (!isOpen) return;

      if (event.key === "Escape") {
        closePreview();
        return;
      }
      if (event.key === "ArrowLeft") {
        showPrev();
        return;
      }
      if (event.key === "ArrowRight") {
        showNext();
      }
    });
  };

  window.addEventListener("load", () => {
    loadEmblaCarousel();
    loadVideoPlayers();
    loadImagePreview();
  });
})();
